import { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { useAppStore } from './store/appStore';
import { useImageStore } from './store/imageStore';
import { showSuccessToast, showErrorToast, showWarningToast } from './store/toastStore';
import { ToastContainer } from './components/common/Toast';
import { LoadingOverlay } from './components/common/LoadingOverlay';
import { WorkspaceLayout } from './layouts/WorkspaceLayout';
import { FileList } from './components/upload/FileList';
import { UploadZone } from './components/upload/UploadZone';
import { PasteHandler } from './components/upload/PasteHandler';
import { Undo, Redo } from 'lucide-react';

// Canvas Views
import { PreviewCanvas } from './components/preview/PreviewCanvas';
// Lazy load CropCanvas to defer Fabric.js loading until needed
const CropCanvas = lazy(() => import('./components/crop/CropCanvas').then(module => ({ default: module.CropCanvas })));

// Tool Controls
import { CompressControls } from './components/compress/CompressControls';
import { ResizeControls } from './components/resize/ResizeControls';
import { RotateFlipControls } from './components/rotate/RotateFlipControls';
import { CropControls } from './components/crop/CropControls';
import { BgRemoveControls } from './components/bgremove/BgRemoveControls';

// Types and Hooks
import { CompressionFormat, ResizeQuality, RotateAngle, FlipDirection, CropRect, OperationType } from './types';
import type { JpegAdvancedParams, WebPAdvancedParams } from './types/wasm';
import { useCompressWorker } from './hooks/useCompressWorker';
import { useCoreWorker } from './hooks/useCoreWorker';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { fileToImageData } from './utils/imageUtils';
import { formatBytes } from './utils/constants';

function App() {
  const { currentFeature, setDarkMode, setLoading } = useAppStore();
  const { addImages, getSelectedImage, applyOperation } = useImageStore();

  const currentImage = getSelectedImage();

  // Initialize dark mode on mount
  useEffect(() => {
    setDarkMode(true);
  }, [setDarkMode]);

  // Enable global keyboard shortcuts
  useKeyboardShortcuts({
    enableUndo: true,
    enableRedo: true,
    enableSave: true,
    enableHelp: true,
    onSave: () => {
      // Custom save handler: download current image
      if (currentImage) {
        const a = document.createElement('a');
        a.href = currentImage.url;
        a.download = currentImage.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    },
  });

  // ============= COMPRESS TOOL STATE =============
  const [compressParams, setCompressParams] = useState<{
    format: CompressionFormat;
    quality: number;
    targetSize?: number;
    advancedParams?: JpegAdvancedParams | WebPAdvancedParams;
  }>({
    format: CompressionFormat.WebP,
    quality: 80,
    targetSize: undefined,
    advancedParams: undefined,
  });
  const [isCompressing, setIsCompressing] = useState(false);

  const { compressJpeg, compressWebp, compressPng, compressToSize, error: wasmError } = useCompressWorker();
  const { cropImage, rotateImage, flipImage, resizeImage, initialized: coreInitialized, loading: coreLoading } = useCoreWorker();

  // ============= RESIZE TOOL STATE =============
  const [resizeParams, setResizeParams] = useState<{
    width: number;
    height: number;
    quality: ResizeQuality;
  }>({
    width: currentImage?.width || 0,
    height: currentImage?.height || 0,
    quality: ResizeQuality.High,
  });
  const [isResizing, setIsResizing] = useState(false);

  // ============= ROTATE/FLIP TOOL STATE =============
  const [rotation, setRotation] = useState<RotateAngle>(0);
  const [isRotating, setIsRotating] = useState(false);

  // ============= CROP TOOL STATE =============
  const [cropRect, setCropRect] = useState<CropRect>({
    x: 0,
    y: 0,
    width: currentImage?.width || 0,
    height: currentImage?.height || 0,
  });
  const [isCropping, setIsCropping] = useState(false);

  // ============= HISTORY STATE (Undo/Redo) =============
  interface HistoryState {
    file: File;
    width: number;
    height: number;
    cropRect: CropRect;
  }

  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Save current state to history
  const saveToHistory = useCallback(() => {
    if (!currentImage) return;

    const state: HistoryState = {
      file: currentImage.file,
      width: currentImage.width,
      height: currentImage.height,
      cropRect: { ...cropRect },
    };

    // Remove any future states if we're not at the end
    const newHistory = historyIndex < history.length - 1
      ? [...history.slice(0, historyIndex + 1), state]
      : [...history, state];

    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);

    // Limit history to 50 states to prevent memory issues
    if (newHistory.length > 50) {
      setHistory(newHistory.slice(-50));
      setHistoryIndex(49);
    }

    console.log('💾 Saved to history, index:', historyIndex + 1, 'total:', newHistory.length);
  }, [currentImage, cropRect, history, historyIndex]);

  // Undo
  const undo = useCallback(async () => {
    if (historyIndex <= 0) {
      console.log('⚠️ Cannot undo - at beginning of history');
      return;
    }

    const newIndex = historyIndex - 1;
    const prevState = history[newIndex];

    if (prevState) {
      await updateCurrentImage(prevState.file, prevState.width, prevState.height);
      setCropRect(prevState.cropRect);
      setHistoryIndex(newIndex);

      console.log('↩️ Undid to index:', newIndex);
    }
  }, [history, historyIndex]);

  // Redo
  const redo = useCallback(async () => {
    if (historyIndex >= history.length - 1) {
      console.log('⚠️ Cannot redo - at end of history');
      return;
    }

    const newIndex = historyIndex + 1;
    const nextState = history[newIndex];

    if (nextState) {
      await updateCurrentImage(nextState.file, nextState.width, nextState.height);
      setCropRect(nextState.cropRect);
      setHistoryIndex(newIndex);

      console.log('↪️ Redid to index:', newIndex);
    }
  }, [history, historyIndex]);

  // Update current image in store
  const updateCurrentImage = useCallback(async (file: File, width: number, height: number) => {
    const { images, selectedImageId } = useImageStore.getState();

    if (!selectedImageId) return;

    // Create new URL
    const url = URL.createObjectURL(file);

    // Revoke old URL
    const oldImage = images.find(img => img.id === selectedImageId);
    if (oldImage) {
      try {
        URL.revokeObjectURL(oldImage.url);
      } catch (e) {
        console.warn('Failed to revoke old URL:', e);
      }
    }

    // Update images array
    setHistory(prevState => {
      const updatedImages = images.map(img =>
        img.id === selectedImageId
          ? { ...img, file, url, width, height, size: file.size }
          : img
      );

      // Update the store
      useImageStore.setState({ images: updatedImages });

      return prevState;
    });
  }, []);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Z or Cmd+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      }
      // Ctrl+Shift+Z or Ctrl+Y or Cmd+Shift+Z for redo
      if (((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) ||
          ((e.ctrlKey || e.metaKey) && e.key === 'y')) {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // ============= BGREMOVE TOOL STATE =============
  const [bgRemoveImageData, setBgRemoveImageData] = useState<Uint8Array | null>(null);

  // Update bgremove imageData when image changes
  useEffect(() => {
    if (currentImage) {
      fileToImageData(currentImage.file).then((imgData) => {
        setBgRemoveImageData(new Uint8Array(imgData.data));
      }).catch(err => {
        console.error('Failed to load image data for bgremove:', err);
      });
    }
  }, [currentImage]);

  // Update crop rect when image changes
  useEffect(() => {
    if (currentImage) {
      setCropRect({
        x: 0,
        y: 0,
        width: currentImage.width,
        height: currentImage.height,
      });
    }
  }, [currentImage]);

  // Update resize params when image changes
  useEffect(() => {
    if (currentImage) {
      setResizeParams({
        width: currentImage.width,
        height: currentImage.height,
        quality: ResizeQuality.High,
      });
    }
  }, [currentImage]);

  // ============= COMPRESS TOOL HANDLERS =============
  const handleCompressChange = useCallback((params: {
    format: CompressionFormat;
    quality: number;
    targetSize?: number;
    advancedParams?: JpegAdvancedParams | WebPAdvancedParams;
  }) => {
    setCompressParams(params);
  }, []);

  const handleApplyCompress = useCallback(async () => {
    if (!currentImage) {
      showWarningToast('No Image', 'Please select an image first');
      return;
    }

    setIsCompressing(true);
    setLoading(true, 'Compressing image...');
    try {
      if (wasmError) {
        throw new Error(`WASM initialization failed: ${wasmError.message}`);
      }

      const imageData = await fileToImageData(currentImage.file);
      const rgbaBuffer = new Uint8Array(imageData.data);

      let result;
      const { format, quality, targetSize } = compressParams;

      if (targetSize) {
        result = await compressToSize(
          rgbaBuffer,
          currentImage.width,
          currentImage.height,
          targetSize,
          format
        );
      } else if (format === CompressionFormat.WebP) {
        result = await compressWebp(
          rgbaBuffer,
          currentImage.width,
          currentImage.height,
          quality,
          compressParams.advancedParams as WebPAdvancedParams
        );
      } else if (format === CompressionFormat.PNG) {
        result = await compressPng(
          rgbaBuffer,
          currentImage.width,
          currentImage.height,
          quality
        );
      } else {
        result = await compressJpeg(
          rgbaBuffer,
          currentImage.width,
          currentImage.height,
          quality,
          compressParams.advancedParams as JpegAdvancedParams
        );
      }

      const mimeTypes = {
        [CompressionFormat.JPEG]: 'image/jpeg',
        [CompressionFormat.WebP]: 'image/webp',
        [CompressionFormat.PNG]: 'image/png',
      };
      const mimeType = mimeTypes[format];
      const extension = mimeType.split('/')[1];
      const originalName = currentImage.file.name.replace(/\.[^/.]+$/, '');
      const newName = `${originalName}_compressed.${extension}`;

      const buffer = new ArrayBuffer(result.imageData.length);
      new Uint8Array(buffer).set(result.imageData);

      // Apply operation to update the main image (instead of downloading)
      const newFile = new File([buffer], newName, { type: mimeType });
      applyOperation(currentImage.id, newFile, {
        type: OperationType.COMPRESS,
        params: {
          format,
          quality: result.quality,
          mode: targetSize ? 'target_size' : 'quality',
        },
        timestamp: Date.now()
      }, currentImage.width, currentImage.height); // Compression doesn't change dimensions

      const mode = targetSize ? 'Target Size' : 'Quality';
      const formatLabel = format === CompressionFormat.WebP ? 'WebP' : format === CompressionFormat.JPEG ? 'JPEG' : 'PNG';
      const savings = Math.round(((currentImage.file.size - result.size) / currentImage.file.size) * 100);

      const details = [
        `Format: ${formatLabel}`,
        `Original: ${formatBytes(currentImage.file.size)}`,
        `Compressed: ${formatBytes(result.size)}`,
        `Quality: ${result.quality}%`,
        `Space saved: ${savings}%`,
        `Mode: ${mode}`,
      ];

      if (targetSize) {
        details.push(`Target: ${formatBytes(targetSize)}`);
      }

      details.push(`File: ${newName}`);

      showSuccessToast('Compression Complete', details.join('\n'));
    } catch (error) {
      console.error('Compression failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showErrorToast('Compression Failed', errorMessage);
    } finally {
      setIsCompressing(false);
      setLoading(false);
    }
  }, [currentImage, wasmError, compressParams, compressToSize, compressWebp, compressJpeg, compressPng, setLoading, applyOperation]);

  const handleResetCompress = useCallback(() => {
    setCompressParams({
      format: CompressionFormat.WebP,
      quality: 80,
      targetSize: undefined,
      advancedParams: undefined,
    });
  }, []);

  // ============= RESIZE TOOL HANDLERS =============
  const handleResizeChange = useCallback((params: { width: number; height: number; quality: ResizeQuality }) => {
    setResizeParams(params);
  }, []);

  const handleApplyResize = useCallback(async () => {
    if (!currentImage) return;

    setIsResizing(true);
    setLoading(true, 'Resizing image...');
    try {
      const { width: newWidth, height: newHeight, quality } = resizeParams;

      // Skip if dimensions are unchanged
      if (newWidth === currentImage.width && newHeight === currentImage.height) {
        showWarningToast('No Changes', 'Image dimensions are unchanged. Please adjust the size first.');
        return;
      }

      // Convert image to RGBA buffer
      const imageData = await fileToImageData(currentImage.file);
      const rgbaBuffer = new Uint8Array(imageData.data);

      // Send to WASM worker for resizing
      const result = await resizeImage(rgbaBuffer, currentImage.width, currentImage.height, newWidth, newHeight, quality);

      // Convert result back to ImageData
      const resizedImageData = new ImageData(
        new Uint8ClampedArray(result.imageData),
        result.width,
        result.height
      );

      // Create result canvas and convert to blob
      const resultCanvas = document.createElement('canvas');
      resultCanvas.width = result.width;
      resultCanvas.height = result.height;
      const resultCtx = resultCanvas.getContext('2d');
      if (!resultCtx) {
        throw new Error('Failed to get canvas context');
      }
      resultCtx.putImageData(resizedImageData, 0, 0);

      // Convert to blob and apply operation (instead of downloading)
      resultCanvas.toBlob((blob) => {
        if (blob) {
          const newFile = new File(
            [blob],
            currentImage.fileName.replace(/\.[^/.]+$/, '_resized.png'),
            { type: 'image/png' }
          );

          applyOperation(currentImage.id, newFile, {
            type: OperationType.RESIZE,
            params: {
              width: newWidth,
              height: newHeight,
              quality,
              mode: 'precise',
              maintainAspectRatio: false,
            },
            timestamp: Date.now()
          }, result.width, result.height);

          const scale = Math.round((newWidth / currentImage.width) * 100);
          const details = [
            `Original: ${currentImage.width} x ${currentImage.height} px`,
            `Resized: ${newWidth} x ${newHeight} px`,
            `Scale: ${scale}%`,
            `Quality: ${quality === ResizeQuality.High ? 'High (Bicubic)' : 'Fast (Bilinear)'}`,
          ];

          showSuccessToast('Resize Complete', details.join('\n'));
        }
      }, 'image/png');

      console.log('Resize applied successfully', {
        from: `${currentImage.width}x${currentImage.height}`,
        to: `${newWidth}x${newHeight}`,
        quality,
      });
    } catch (error) {
      console.error('Resize failed:', error);
      showErrorToast('Resize Failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsResizing(false);
      setLoading(false);
    }
  }, [currentImage, resizeParams, resizeImage, setLoading, applyOperation]);

  const handleResetResize = useCallback(() => {
    if (currentImage) {
      setResizeParams({
        width: currentImage.width,
        height: currentImage.height,
        quality: ResizeQuality.High,
      });
    }
  }, [currentImage]);

  // ============= ROTATE/FLIP TOOL HANDLERS =============
  const handleRotateChange = useCallback(async (angle: RotateAngle) => {
    if (!currentImage) return;

    setRotation(angle);
    setIsRotating(true);
    setLoading(true, `Rotating image ${angle}°...`);

    try {
      // Convert image to RGBA buffer
      const imageData = await fileToImageData(currentImage.file);
      const rgbaBuffer = new Uint8Array(imageData.data);

      // Send to WASM worker for rotation
      const result = await rotateImage(rgbaBuffer, currentImage.width, currentImage.height, angle);

      // Convert result back to ImageData
      const rotatedImageData = new ImageData(
        new Uint8ClampedArray(result.imageData),
        result.width,
        result.height
      );

      // Create result canvas and convert to blob
      const resultCanvas = document.createElement('canvas');
      resultCanvas.width = result.width;
      resultCanvas.height = result.height;
      const resultCtx = resultCanvas.getContext('2d');
      if (!resultCtx) {
        throw new Error('Failed to get canvas context');
      }
      resultCtx.putImageData(rotatedImageData, 0, 0);

      // Convert to blob and apply operation (instead of downloading)
      resultCanvas.toBlob((blob) => {
        if (blob) {
          const newFile = new File(
            [blob],
            currentImage.fileName.replace(/\.[^/.]+$/, `_rotated_${angle}.png`),
            { type: 'image/png' }
          );

          applyOperation(currentImage.id, newFile, {
            type: OperationType.ROTATE,
            params: { angle, autoFixEXIF: false },
            timestamp: Date.now()
          }, result.width, result.height);

          const details = [
            `Angle: ${angle}deg`,
            `Original: ${currentImage.width} x ${currentImage.height} px`,
            `Result: ${result.width} x ${result.height} px`,
          ];

          showSuccessToast('Rotation Complete', details.join('\n'));
        }
      }, 'image/png');

      console.log('Rotation applied successfully', {
        angle,
        from: `${currentImage.width}x${currentImage.height}`,
        to: `${result.width}x${result.height}`,
      });
    } catch (error) {
      console.error('Rotation failed:', error);
      showErrorToast('Rotation Failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsRotating(false);
      setLoading(false);
    }
  }, [currentImage, rotateImage, setLoading, applyOperation]);

  const handleFlipHorizontal = useCallback(async () => {
    if (!currentImage) return;

    setIsRotating(true);
    setLoading(true, 'Flipping image horizontally...');
    try {
      // Convert image to RGBA buffer
      const imageData = await fileToImageData(currentImage.file);
      const rgbaBuffer = new Uint8Array(imageData.data);

      // Send to WASM worker for flipping
      const result = await flipImage(rgbaBuffer, currentImage.width, currentImage.height, FlipDirection.Horizontal);

      // Convert result back to ImageData
      const flippedImageData = new ImageData(
        new Uint8ClampedArray(result.imageData),
        result.width,
        result.height
      );

      // Create result canvas and convert to blob
      const resultCanvas = document.createElement('canvas');
      resultCanvas.width = result.width;
      resultCanvas.height = result.height;
      const resultCtx = resultCanvas.getContext('2d');
      if (!resultCtx) {
        throw new Error('Failed to get canvas context');
      }
      resultCtx.putImageData(flippedImageData, 0, 0);

      // Convert to blob and apply operation (instead of downloading)
      resultCanvas.toBlob((blob) => {
        if (blob) {
          const newFile = new File(
            [blob],
            currentImage.fileName.replace(/\.[^/.]+$/, '_flipped_h.png'),
            { type: 'image/png' }
          );

          applyOperation(currentImage.id, newFile, {
            type: OperationType.FLIP,
            params: { direction: FlipDirection.Horizontal },
            timestamp: Date.now()
          }, result.width, result.height);

          showSuccessToast('Horizontal Flip Complete', `Size: ${currentImage.width} x ${currentImage.height} px`);
        }
      }, 'image/png');

      console.log('Horizontal flip applied successfully');
    } catch (error) {
      console.error('Flip failed:', error);
      showErrorToast('Flip Failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsRotating(false);
      setLoading(false);
    }
  }, [currentImage, flipImage, setLoading, applyOperation]);

  const handleFlipVertical = useCallback(async () => {
    if (!currentImage) return;

    setIsRotating(true);
    setLoading(true, 'Flipping image vertically...');
    try {
      // Convert image to RGBA buffer
      const imageData = await fileToImageData(currentImage.file);
      const rgbaBuffer = new Uint8Array(imageData.data);

      // Send to WASM worker for flipping
      const result = await flipImage(rgbaBuffer, currentImage.width, currentImage.height, FlipDirection.Vertical);

      // Convert result back to ImageData
      const flippedImageData = new ImageData(
        new Uint8ClampedArray(result.imageData),
        result.width,
        result.height
      );

      // Create result canvas and convert to blob
      const resultCanvas = document.createElement('canvas');
      resultCanvas.width = result.width;
      resultCanvas.height = result.height;
      const resultCtx = resultCanvas.getContext('2d');
      if (!resultCtx) {
        throw new Error('Failed to get canvas context');
      }
      resultCtx.putImageData(flippedImageData, 0, 0);

      // Convert to blob and apply operation (instead of downloading)
      resultCanvas.toBlob((blob) => {
        if (blob) {
          const newFile = new File(
            [blob],
            currentImage.fileName.replace(/\.[^/.]+$/, '_flipped_v.png'),
            { type: 'image/png' }
          );

          applyOperation(currentImage.id, newFile, {
            type: OperationType.FLIP,
            params: { direction: FlipDirection.Vertical },
            timestamp: Date.now()
          }, result.width, result.height);

          showSuccessToast('Vertical Flip Complete', `Size: ${currentImage.width} x ${currentImage.height} px`);
        }
      }, 'image/png');

      console.log('Vertical flip applied successfully');
    } catch (error) {
      console.error('Flip failed:', error);
      showErrorToast('Flip Failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsRotating(false);
      setLoading(false);
    }
  }, [currentImage, flipImage, setLoading, applyOperation]);

  const handleResetRotate = useCallback(() => {
    setRotation(0);
  }, []);

  // ============= CROP TOOL HANDLERS =============
  const handleCropChange = useCallback((rect: CropRect) => {
    console.log('📦 handleCropChange:', rect);

    // ✅ Only update if values actually changed
    setCropRect((prev) => {
      if (prev.x === rect.x && prev.y === rect.y &&
          prev.width === rect.width && prev.height === rect.height) {
        return prev; // Avoid unnecessary updates
      }
      return rect;
    });
  }, []);

  const handleApplyCrop = useCallback(async () => {
    if (!currentImage) return;

    // ✅ Check if WASM worker is initialized
    if (!coreInitialized || coreLoading) {
      alert('WASM module is still loading. Please wait a moment and try again.');
      return;
    }

    setIsCropping(true);
    setLoading(true, 'Cropping image...');
    try {
      // ✅ Save current state to history before applying crop
      saveToHistory();

      // Convert image to RGBA buffer
      const imageData = await fileToImageData(currentImage.file);
      const rgbaBuffer = new Uint8Array(imageData.data);

      console.log('🔧 Calling cropImage with:', {
        width: currentImage.width,
        height: currentImage.height,
        cropRect,
        bufferSize: rgbaBuffer.length,
      });

      // Send to WASM worker for cropping
      const result = await cropImage(rgbaBuffer, currentImage.width, currentImage.height, cropRect);

      // Convert result back to ImageData
      const croppedImageData = new ImageData(
        new Uint8ClampedArray(result.imageData),
        result.width,
        result.height
      );

      // Create result canvas and convert to blob
      const resultCanvas = document.createElement('canvas');
      resultCanvas.width = result.width;
      resultCanvas.height = result.height;
      const resultCtx = resultCanvas.getContext('2d');
      if (!resultCtx) {
        throw new Error('Failed to get canvas context');
      }
      resultCtx.putImageData(croppedImageData, 0, 0);

      // Convert to blob and apply operation (instead of downloading)
      resultCanvas.toBlob((blob) => {
        if (blob) {
          const newFile = new File(
            [blob],
            currentImage.fileName.replace(/\.[^/.]+$/, '_cropped.png'),
            { type: 'image/png' }
          );

          applyOperation(currentImage.id, newFile, {
            type: OperationType.CROP,
            params: cropRect,
            timestamp: Date.now()
          }, result.width, result.height);

          // Reset crop selection to full image (which is now the cropped result)
          setCropRect({ x: 0, y: 0, width: result.width, height: result.height });

          const details = [
            `Original: ${currentImage.width} x ${currentImage.height} px`,
            `Cropped: ${result.width} x ${result.height} px`,
            `Region: x=${cropRect.x}, y=${cropRect.y}, w=${cropRect.width}, h=${cropRect.height}`,
          ];

          showSuccessToast('Crop Complete', details.join('\n'));
        }
      }, 'image/png');

      console.log('Crop applied successfully', {
        from: `${currentImage.width}x${currentImage.height}`,
        to: `${result.width}x${result.height}`,
        cropRect,
      });
    } catch (error) {
      console.error('Crop failed:', error);
      showErrorToast('Crop Failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsCropping(false);
      setLoading(false);
    }
  }, [currentImage, cropRect, cropImage, setLoading, applyOperation]);

  const handleResetCrop = useCallback(() => {
    if (currentImage) {
      setCropRect({
        x: 0,
        y: 0,
        width: currentImage.width,
        height: currentImage.height,
      });
    }
  }, [currentImage]);

  // ============= BGREMOVE TOOL HANDLERS =============
  const handleBgRemoveComplete = useCallback(async (result: ArrayBuffer, width: number, height: number) => {
    if (!currentImage) return;

    try {
      // Convert result to ImageData
      const resultImageData = new ImageData(
        new Uint8ClampedArray(result),
        width,
        height
      );

      // Create result canvas and convert to blob
      const resultCanvas = document.createElement('canvas');
      resultCanvas.width = width;
      resultCanvas.height = height;
      const resultCtx = resultCanvas.getContext('2d');
      if (!resultCtx) {
        throw new Error('Failed to get canvas context');
      }
      resultCtx.putImageData(resultImageData, 0, 0);

      // Convert to blob and apply operation (instead of downloading)
      resultCanvas.toBlob((blob) => {
        if (blob) {
          const newFile = new File(
            [blob],
            currentImage.fileName.replace(/\.[^/.]+$/, '_bgremoved.png'),
            { type: 'image/png' }
          );

          applyOperation(currentImage.id, newFile, {
            type: OperationType.REMOVE_BACKGROUND,
            params: {
              method: 'magic_wand', // Default method
            },
            timestamp: Date.now()
          }, width, height);

          showSuccessToast('Background Removal Complete', 'Image updated successfully');
        }
      }, 'image/png');

      console.log('Background removal applied successfully');
    } catch (error) {
      console.error('Background removal failed:', error);
      showErrorToast('Background Removal Failed', error instanceof Error ? error.message : 'Unknown error');
    }
  }, [currentImage, applyOperation]);

  const handleFilesSelected = async (files: File[]) => {
    console.log('📁 Files selected:', files.map(f => f.name));
    await addImages(files);
  };

  // Render controls for the right sidebar based on current feature
  const renderControls = () => {
    switch (currentFeature) {
      case 'upload':
        return (
          <div className="space-y-4">
            <div className="text-sm text-zinc-400 mb-4">
              <p className="mb-2">Upload images to get started:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Drag & drop files onto the canvas</li>
                <li>Click the button below</li>
                <li>Paste from clipboard (Ctrl+V)</li>
              </ul>
            </div>
            <UploadZone onFilesSelected={handleFilesSelected} compact />
            <div className="mt-4 text-xs text-zinc-500 bg-zinc-800/50 p-3 rounded-lg border border-zinc-700">
              <p className="font-semibold mb-1">Privacy First</p>
              <p>All processing happens in your browser. Images never leave your device.</p>
            </div>
          </div>
        );

      case 'crop':
        return currentImage ? (
          <CropControls
            currentCrop={cropRect}
            imageSize={{ width: currentImage.width, height: currentImage.height }}
            onCropChange={handleCropChange}
            onApply={handleApplyCrop}
            onReset={handleResetCrop}
            isProcessing={isCropping}
          />
        ) : (
          <div className="text-sm text-zinc-400">
            <p>No image selected.</p>
            <p className="mt-2 text-xs">Upload an image or select from the filmstrip below.</p>
          </div>
        );

      case 'rotate':
        return currentImage ? (
          <RotateFlipControls
            currentRotation={rotation}
            onRotateChange={handleRotateChange}
            onFlipHorizontal={handleFlipHorizontal}
            onFlipVertical={handleFlipVertical}
            onReset={handleResetRotate}
            isProcessing={isRotating}
          />
        ) : (
          <div className="text-sm text-zinc-400">
            <p>No image selected.</p>
            <p className="mt-2 text-xs">Upload an image or select from the filmstrip below.</p>
          </div>
        );

      case 'resize':
        return currentImage ? (
          <ResizeControls
            currentSize={{ width: currentImage.width, height: currentImage.height }}
            onResizeChange={handleResizeChange}
            onApply={handleApplyResize}
            onReset={handleResetResize}
            isProcessing={isResizing}
          />
        ) : (
          <div className="text-sm text-zinc-400">
            <p>No image selected.</p>
            <p className="mt-2 text-xs">Upload an image or select from the filmstrip below.</p>
          </div>
        );

      case 'compress':
        return currentImage ? (
          <CompressControls
            originalSize={currentImage.file.size}
            originalFormat={currentImage.file.type.split('/')[1].toUpperCase()}
            onCompressChange={handleCompressChange}
            onApply={handleApplyCompress}
            onReset={handleResetCompress}
            isProcessing={isCompressing}
          />
        ) : (
          <div className="text-sm text-zinc-400">
            <p>No image selected.</p>
            <p className="mt-2 text-xs">Upload an image or select from the filmstrip below.</p>
          </div>
        );

      case 'bgremove':
        return currentImage && bgRemoveImageData ? (
          <BgRemoveControls
            imageData={bgRemoveImageData}
            width={currentImage.width}
            height={currentImage.height}
            onRemoveComplete={handleBgRemoveComplete}
          />
        ) : (
          <div className="text-sm text-zinc-400">
            <p>No image selected.</p>
            <p className="mt-2 text-xs">Upload an image or select from the filmstrip below.</p>
          </div>
        );

      case 'batch':
        return (
          <div className="space-y-4">
            <div className="text-sm text-zinc-400 mb-4">
              <p className="font-semibold text-zinc-300 mb-2">📦 Batch Processing</p>
              <p className="text-xs">Apply the same operation to multiple images at once:</p>
              <ul className="list-disc list-inside space-y-1 text-xs mt-2">
                <li>Select multiple images from the filmstrip</li>
                <li>Choose an operation (crop, resize, compress, etc.)</li>
                <li>Download all processed images as a ZIP file</li>
              </ul>
            </div>
            <div className="mt-4 p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg">
              <p className="text-sm text-amber-200">
                ⚠️ <strong>Coming Soon</strong>
              </p>
              <p className="text-xs text-amber-300 mt-2">
                Batch processing UI is under development. The backend workers are ready!
              </p>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-sm text-zinc-400">
            Select a tool from the sidebar
          </div>
        );
    }
  };

  // Render canvas in the center based on current feature
  const renderCanvas = () => {
    // If no image is loaded, show the upload zone
    if (!currentImage) {
      return (
        <div className="flex flex-col items-center justify-center gap-6 w-full max-w-2xl">
          <div className="text-center mb-4">
            <h2 className="text-2xl font-bold text-zinc-200 mb-2">Welcome to Photo Editor</h2>
            <p className="text-zinc-400">Get started by uploading an image</p>
          </div>
          <UploadZone onFilesSelected={handleFilesSelected} />
          <div className="text-xs text-zinc-500 text-center max-w-md">
            <p className="mb-2">💡 <strong>Privacy First:</strong> All processing happens in your browser</p>
            <p>Supported formats: JPEG, PNG, WebP, GIF • Max size: 50MB</p>
          </div>
        </div>
      );
    }

    // Feature-specific canvas rendering
    switch (currentFeature) {
      case 'crop':
        // Use CropCanvas for interactive cropping (lazy loaded with Fabric.js)
        return (
          <Suspense fallback={
            <div className="flex items-center justify-center w-full h-full">
              <div className="text-zinc-400">Loading crop tool...</div>
            </div>
          }>
            <CropCanvas
              imageId={currentImage.id}
              imageData={{
                url: currentImage.url,
                width: currentImage.width,
                height: currentImage.height,
              }}
              initialCropRect={cropRect}
              onCropChange={handleCropChange}
            />
          </Suspense>
        );

      default:
        return <PreviewCanvas />;
    }
  };

  return (
    <>
      <PasteHandler onFilesSelected={handleFilesSelected} />
      <WorkspaceLayout
        propertiesPanel={renderControls()}
        bottomPanel={<FileList variant="filmstrip" />}
        undoRedoButtons={
          <div className="flex items-center gap-1">
            <button
              onClick={undo}
              disabled={historyIndex <= 0}
              className="p-2 rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Undo (Ctrl+Z)"
            >
              <Undo size={16} />
            </button>
            <button
              onClick={redo}
              disabled={historyIndex >= history.length - 1}
              className="p-2 rounded-lg text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Redo (Ctrl+Shift+Z or Ctrl+Y)"
            >
              <Redo size={16} />
            </button>
          </div>
        }
      >
        {renderCanvas()}
      </WorkspaceLayout>
      <ToastContainer />
      <LoadingOverlay />
    </>
  );
}

export default App;
